import { useState, useMemo } from 'react';
import { useSearchParams, useNavigationAdapter } from '../../navigation';
import { Eye, EyeOff } from 'lucide-react';
import { AuthPasswordStrengthIndicator } from './AuthPasswordStrengthIndicator';
import type { InputHTMLAttributes } from 'react';
import type { AuthConfig } from '../../types';
import { resolveAuthUrl } from '../../lib/path-utils';
import {
  PasswordFieldWrapper,
  PasswordFieldLabelRow,
  PasswordFieldLabel,
  PasswordFieldForgotLink,
  PasswordFieldInputWrapper,
  PasswordFieldInput,
  PasswordFieldToggleButton,
} from './styled';

export interface AuthPasswordFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  id: string;
  showStrengthIndicator?: boolean;
  authConfig: AuthConfig;
  forgotPasswordLink?: {
    href: string;
    text?: string;
  };
}

/**
 * Password input field with visibility toggle and optional strength indicator.
 */
export function AuthPasswordField({
  label,
  id,
  showStrengthIndicator = false,
  authConfig,
  forgotPasswordLink,
  value,
  onFocus,
  ...props
}: AuthPasswordFieldProps) {
  const searchParams = useSearchParams();
  const { Link } = useNavigationAdapter();
  const [showPassword, setShowPassword] = useState(false);
  const [showStrength, setShowStrength] = useState(false);

  // Resolve forgot password link with proper base path handling
  const resolvedForgotPasswordHref = useMemo(
    () => (forgotPasswordLink ? resolveAuthUrl(forgotPasswordLink.href, searchParams) : undefined),
    [forgotPasswordLink, searchParams]
  );

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (showStrengthIndicator) {
      setShowStrength(true);
    }
    onFocus?.(e);
  };

  return (
    <PasswordFieldWrapper>
      {(label || forgotPasswordLink) && (
        <PasswordFieldLabelRow>
          <PasswordFieldLabel htmlFor={id}>{label}</PasswordFieldLabel>
          {forgotPasswordLink && resolvedForgotPasswordHref && (
            <Link href={resolvedForgotPasswordHref} className="">
              <PasswordFieldForgotLink as="span">
                {forgotPasswordLink.text || 'Forget Password?'}
              </PasswordFieldForgotLink>
            </Link>
          )}
        </PasswordFieldLabelRow>
      )}
      <PasswordFieldInputWrapper>
        <PasswordFieldInput
          id={id}
          type={showPassword ? 'text' : 'password'}
          value={value}
          onFocus={handleFocus}
          {...props}
        />
        <PasswordFieldToggleButton
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          aria-label={showPassword ? 'Hide password' : 'Show password'}
        >
          {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
        </PasswordFieldToggleButton>
      </PasswordFieldInputWrapper>
      {showStrengthIndicator && showStrength && (
        <AuthPasswordStrengthIndicator password={String(value || '')} config={authConfig} />
      )}
    </PasswordFieldWrapper>
  );
}
